import type { FAQItem } from '@/components/section/faq/Faq';
import type { SpecialLinkCard } from '@/components/pages/marketing/special/types';
import { helpFaqItemId } from '@/constants/helpCenter';
import { isKnownMarketingPath } from '@/lib/routing/marketingHref';

export type HelpSearchResultKind = 'category' | 'quickLink' | 'faq';

export type HelpSearchResult = {
  kind: HelpSearchResultKind;
  title: string;
  text: string;
  href: string;
  faqId?: string;
};

function haystack(title: string, text: string): string {
  return `${title} ${text}`.toLowerCase();
}

export function filterHelpTopics(
  query: string,
  categories: readonly SpecialLinkCard[],
  quickLinks: readonly SpecialLinkCard[],
  faqItems: readonly FAQItem[]
): HelpSearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const out: HelpSearchResult[] = [];

  for (const card of categories) {
    if (haystack(card.title, card.text).includes(q)) {
      out.push({
        kind: 'category',
        title: card.title,
        text: card.text,
        href: card.href,
      });
    }
  }

  for (const link of quickLinks) {
    if (haystack(link.title, link.text || link.title).includes(q)) {
      out.push({
        kind: 'quickLink',
        title: link.title,
        text: link.text,
        href: link.href,
      });
    }
  }

  for (const item of faqItems) {
    const question = typeof item.question === 'string' ? item.question : '';
    const answer = typeof item.answer === 'string' ? item.answer : '';
    if (haystack(question, answer).includes(q)) {
      out.push({
        kind: 'faq',
        title: question,
        text: answer,
        href: `#${helpFaqItemId(item.id)}`,
        faqId: item.id,
      });
    }
  }

  return out;
}

/** Resolve help cards / links — drops unknown paths (dev warning). */
export function resolveHelpLinkCards(
  cards: readonly SpecialLinkCard[],
  options?: { warn?: boolean }
): SpecialLinkCard[] {
  return cards.filter((card) => {
    const ok = isKnownMarketingPath(card.href);
    if (!ok && options?.warn !== false && process.env.NODE_ENV === 'development') {
      console.warn(`[help] Unknown marketing path: ${card.href}`);
    }
    return ok;
  });
}
